import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { apiGet } from "../lib/api";
import { storeWorkspaces, type WorkspaceSession, type WorkspaceSummary } from "../lib/workspaces";

interface VerifyResult {
  needsRegistration: boolean;
  isNewUser?: boolean;
  email?: string;
  token?: string;
  tenant?: { id: string; name: string; slug: string };
  user?: { id: string; email: string; name: string | null; role: string };
  selectionToken?: string;
  workspaces?: WorkspaceSummary[];
}

export function Verify() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(true);
  const calledRef = useRef(false);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    const token = searchParams.get("token");
    if (!token) {
      setError("Missing token");
      setVerifying(false);
      return;
    }

    apiGet<VerifyResult>(`/auth/verify?token=${token}`)
      .then((res) => {
        if (!res.success || !res.data) {
          setError(res.error?.message || "Verification failed");
          setVerifying(false);
          return;
        }

        if (res.data.needsRegistration) {
          setError("No account found for this email. Please register first.");
          setVerifying(false);
          return;
        }

        if (res.data.selectionToken && res.data.workspaces) {
          storeWorkspaces(res.data.workspaces);
          sessionStorage.setItem("vms_selection_token", res.data.selectionToken);
          sessionStorage.setItem("vms_selection_workspaces", JSON.stringify(res.data.workspaces));
          navigate("/workspaces", { replace: true });
          return;
        }

        if (res.data.token && res.data.tenant && res.data.user) {
          login(res.data as WorkspaceSession);
          const hasIntendedPlan = Boolean(localStorage.getItem("vms_intended_plan"));
          const prefix = `/w/${res.data.tenant.slug}`;
          const dest = hasIntendedPlan ? `${prefix}/billing` : res.data.isNewUser ? "/welcome" : `${prefix}/checkins`;
          if (res.data.isNewUser) {
            window.dataLayer?.push({
              event: "registration_complete",
              user_id: res.data.user?.id,
              tenant_id: res.data.tenant?.id,
              eventCallback: () => navigate(dest, { replace: true }),
              eventTimeout: 2000,
            });
          } else {
            navigate(dest, { replace: true });
          }
        }
      })
      .catch(() => {
        setError("Network error");
        setVerifying(false);
      });
  }, [searchParams, navigate, login]);

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 px-4">
        <div className="w-full max-w-sm text-center">
          <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-8">
            <p className="text-sm text-stone-500">Verifying your link...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 px-4">
      <div className="w-full max-w-sm text-center">
        <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-8">
          <h1 className="text-xl font-semibold text-stone-900 mb-2 font-display">Verification failed</h1>
          <p className="text-sm text-red-600 mb-4">{error}</p>
          <Link to="/login" className="text-sm text-brand-500 hover:underline font-medium">
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
